import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { TRIAGE_STEPS } from "@/components/StepNav";
import { Button } from "@/components/ui/button";
import { Phone, PhoneCall, Headset, Layers } from "lucide-react";
import { toast } from "sonner";
import { TRIAGE_SCENARIOS, totalFlags, gradeCall, shuffleScenarioOptions } from "@/data/triage";
import { useCaseList } from "@/context/ContentContext";
import { FurtherReading } from "@/components/FurtherReading";
import { SaveSession } from "@/components/SaveSession";
import {
  RadarPill,
  CallConsole,
  Transcript,
  DecisionPanel,
  TriagePanel,
  CompleteGate,
  Feedback,
  ShiftSummary,
} from "@/components/triage/TriageParts";

const SHIFT_LENGTH = 3;

const pickRandom = (scenarios, excludeIds = []) => {
  const pool = scenarios.filter((s) => !excludeIds.includes(s.id));
  const list = pool.length ? pool : scenarios;
  return list[Math.floor(Math.random() * list.length)];
};

export default function Triage() {
  const navigate = useNavigate();
  const scenarios = useCaseList("triage", TRIAGE_SCENARIOS);

  const [stage, setStage] = useState("intro"); // intro | call | feedback | shiftSummary
  const [mode, setMode] = useState("quick"); // quick | shift
  const [callNo, setCallNo] = useState(1);
  const [usedIds, setUsedIds] = useState([]);
  const [shiftResults, setShiftResults] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [history, setHistory] = useState([]);
  const [beatIdx, setBeatIdx] = useState(0); // number | "triage"
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [picks, setPicks] = useState([]);
  const [triagePick, setTriagePick] = useState(null);
  const [tapped, setTapped] = useState(new Set());
  const [caught, setCaught] = useState(new Set());
  const [falseTaps, setFalseTaps] = useState(0);
  const [result, setResult] = useState(null);

  const total = scenario ? totalFlags(scenario) : 0;
  const totalTurns = scenario ? scenario.beats.length * 2 + 2 : 0;

  const callerEntry = (s, i) => ({
    role: "caller",
    name: s.caller,
    text: s.beats[i].caller,
    chips: s.beats[i].chips || [],
    active: true,
    id: `caller-${i}`,
  });

  const beginCall = (excludeIds = []) => {
    const s = shuffleScenarioOptions(pickRandom(scenarios, excludeIds));
    setUsedIds([...excludeIds, s.id]);
    setScenario(s);
    setHistory([
      { role: "narration", text: `Phone rings. Caller: ${s.caller}.`, id: "narration" },
      callerEntry(s, 0),
    ]);
    setBeatIdx(0);
    setCompleted(false);
    setPicks([]);
    setTriagePick(null);
    setTapped(new Set());
    setCaught(new Set());
    setFalseTaps(0);
    setResult(null);
    setStage("call");
  };

  const startQuick = () => {
    setMode("quick");
    setShiftResults([]);
    setCallNo(1);
    beginCall([]);
  };

  const startShift = () => {
    setMode("shift");
    setShiftResults([]);
    setCallNo(1);
    beginCall([]);
  };

  // Advance from a feedback screen.
  const handleNext = () => {
    if (mode === "shift") {
      if (callNo >= SHIFT_LENGTH) {
        setStage("shiftSummary");
        return;
      }
      setCallNo((n) => n + 1);
      beginCall(usedIds);
    } else {
      beginCall(scenario ? [scenario.id] : []);
    }
  };

  const nextLabel =
    mode === "shift"
      ? callNo >= SHIFT_LENGTH
        ? "View shift summary"
        : `Next call · ${callNo + 1} of ${SHIFT_LENGTH}`
      : "Take another call";

  const lockActive = (arr) => arr.map((e) => (e.role === "caller" ? { ...e, active: false } : e));

  const onTap = (chip) => {
    if (tapped.has(chip.t)) return;
    setTapped((prev) => new Set(prev).add(chip.t));
    if (chip.flag) {
      setCaught((prev) => new Set(prev).add(chip.t));
      toast.success("Red flag caught");
    } else {
      setFalseTaps((n) => n + 1);
      toast.error("Not a red flag — that's a benign detail");
    }
  };

  const pickOption = (optIdx) => {
    const i = beatIdx;
    const beat = scenario.beats[i];
    const maxScore = Math.max(...beat.options.map((o) => o.score ?? 0));
    const correct = (beat.options[optIdx].score ?? 0) === maxScore && maxScore > 0;
    setPicks((prev) => {
      const next = [...prev];
      next[i] = optIdx;
      return next;
    });
    setHistory((prev) => {
      const locked = lockActive(prev);
      locked.push({ role: "you", text: beat.options[optIdx].t, id: `you-${i}`, correct });
      if (i + 1 < scenario.beats.length) {
        locked.push(callerEntry(scenario, i + 1));
      }
      return locked;
    });
    if (i + 1 < scenario.beats.length) setBeatIdx(i + 1);
    else setBeatIdx("triage");
  };

  const pickTriage = (idx) => {
    setTriagePick(idx);
    const maxScore = Math.max(...scenario.triage.options.map((o) => o.score ?? 0));
    const correct =
      (scenario.triage.options[idx].score ?? 0) === maxScore && maxScore > 0;
    setHistory((prev) => {
      const locked = lockActive(prev);
      locked.push({
        role: "triage",
        text: scenario.triage.options[idx].t,
        id: "triage",
        correct,
      });
      return locked;
    });
    setCompleted(true);
  };

  const submit = () => {
    setSubmitting(true);
    // brief delay to mirror the "grading" beat — purely cosmetic, fully local
    setTimeout(() => {
      const r = gradeCall(scenario, {
        picks,
        triagePick,
        caught: Array.from(caught),
        falseTaps,
      });
      setResult(r);
      if (mode === "shift") setShiftResults((prev) => [...prev, r]);
      setStage("feedback");
      setSubmitting(false);
    }, 650);
  };

  // current turn number for the counter (caller line + your reply per beat)
  const turn = useMemo(() => {
    if (!scenario) return 0;
    const answered = picks.filter((p) => p != null).length;
    if (completed) return totalTurns;
    return Math.min(totalTurns, answered * 2 + 1);
  }, [scenario, picks, completed, totalTurns]);

  /* ---------------- intro ---------------- */
  if (stage === "intro") {
    return (
      <div className="min-h-screen bg-[#F4F7F9]">
        <StepNav
          current={1}
          steps={TRIAGE_STEPS}
          title="Ring-in triage"
          subtitle="Cert IV phone-triage trainer"
        />
        <main className="max-w-3xl mx-auto px-6 py-16 fade-up">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Headset className="w-3.5 h-3.5" />
            <span>Riverside Vet · Cert IV simulator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Ring-In Triage Console
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl">
            Take the call. Catch the red flags as they&apos;re spoken. Decide history, advice and
            urgency. You&apos;re graded against a Cert IV rubric with assessor coaching at the end.
          </p>

          <div className="mt-8 rounded-2xl p-6 bg-[#0b1726] border border-[#1d3350] text-slate-200 max-w-md">
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-wider text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-amber-400" /> LINE 1 · STANDBY
            </div>
            <div className="text-[11px] font-mono text-slate-500 mt-1">
              {scenarios.length} CALLS LOADED · CHOOSE A MODE
            </div>
            <div className="mt-4 text-lg font-semibold text-slate-100">The phone&apos;s ringing.</div>
            <p className="text-sm text-slate-400 mt-1">
              You don&apos;t know who&apos;s on the line until you pick up. Take one call, or work a full
              three-call shift for an aggregated Cert IV report.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <Button
                onClick={startQuick}
                data-testid="take-call-btn"
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <PhoneCall className="w-4 h-4 mr-1.5" /> Quick call
              </Button>
              <Button
                onClick={startShift}
                data-testid="start-shift-btn"
                className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-white"
              >
                <Layers className="w-4 h-4 mr-1.5" /> Triage shift · {SHIFT_LENGTH} calls
              </Button>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 mt-6">
            Runs entirely in your browser. No login. Nothing saved. Scenarios are served at random.
          </p>
          <Button
            variant="ghost"
            onClick={() => navigate("/sections")}
            data-testid="intro-home-btn"
            className="mt-2 text-slate-500"
          >
            ← Back to Simulator Hub
          </Button>
        </main>
      </div>
    );
  }

  /* ---------------- feedback ---------------- */
  if (stage === "feedback" && result) {
    return (
      <div className="min-h-screen bg-[#F4F7F9]">
        <StepNav
          current={1}
          steps={TRIAGE_STEPS}
          title="Ring-in triage"
          subtitle={mode === "shift" ? `Shift · call ${callNo} of ${SHIFT_LENGTH}` : "Call debrief"}
        />
        <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
          {mode === "shift" && (
            <div
              className="mb-4 text-[11px] uppercase tracking-wider text-[#0047AB] font-semibold"
              data-testid="shift-progress"
            >
              Triage shift · call {callNo} of {SHIFT_LENGTH}
            </div>
          )}
          <Feedback
            result={result}
            onNext={handleNext}
            nextLabel={nextLabel}
            onHome={() => navigate("/sections")}
          />
          {mode === "quick" && (
            <div className="mt-6">
              <SaveSession
                simulatorId="triage"
                simulatorName="Ring-In Triage Console"
                score={result.score}
                grade={result.verdict.label}
                summary={`${result.verdict.label} · ${result.caughtCount}/${result.totalFlags} red flags`}
              />
            </div>
          )}
        </main>
      </div>
    );
  }

  /* ---------------- shift summary ---------------- */
  if (stage === "shiftSummary" && shiftResults.length > 0) {
    const shiftAvg = Math.round(
      shiftResults.reduce((a, r) => a + r.score, 0) / shiftResults.length
    );
    const shiftGrade =
      shiftAvg >= 85 ? "Competent" : shiftAvg >= 70 ? "Needs revision" : "Not yet competent";
    return (
      <div className="min-h-screen bg-[#F4F7F9]">
        <StepNav current={1} steps={TRIAGE_STEPS} title="Ring-in triage" subtitle="Shift report" />
        <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
          <ShiftSummary
            results={shiftResults}
            onNewShift={startShift}
            onHome={() => navigate("/sections")}
          />
          <div className="mt-6">
            <SaveSession
              simulatorId="triage"
              simulatorName="Ring-In Triage Shift"
              score={shiftAvg}
              grade={shiftGrade}
              summary={`${shiftResults.length}-call shift · ${shiftGrade}`}
            />
          </div>
        </main>
      </div>
    );
  }

  /* ---------------- live call ---------------- */
  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={1}
        steps={TRIAGE_STEPS}
        title="Ring-in triage"
        subtitle={mode === "shift" ? `Live · call ${callNo} of ${SHIFT_LENGTH}` : "Live · Line 1"}
      />
      <main className="max-w-6xl mx-auto px-6 py-6 fade-up">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setStage("intro")}
              data-testid="end-call-btn"
              className="text-slate-600"
            >
              <Phone className="w-4 h-4 mr-1.5" /> End {mode === "shift" ? "shift" : "call"}
            </Button>
            {mode === "shift" && (
              <span
                className="text-[11px] uppercase tracking-wider text-[#0047AB] font-semibold"
                data-testid="call-progress"
              >
                Call {callNo} / {SHIFT_LENGTH}
              </span>
            )}
          </div>
          <RadarPill caught={caught.size} total={total} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:sticky lg:top-24">
            <CallConsole scenario={scenario} />
          </div>

          <div className="lg:col-span-2 space-y-5">
            <Transcript
              history={history}
              turn={turn}
              total={totalTurns}
              onTap={onTap}
              tapped={tapped}
              caught={caught}
            />

            {!completed && beatIdx !== "triage" && (
              <DecisionPanel beat={scenario.beats[beatIdx]} onPick={pickOption} />
            )}
            {!completed && beatIdx === "triage" && (
              <TriagePanel triage={scenario.triage} onPick={pickTriage} />
            )}
            {completed && <CompleteGate onSubmit={submit} submitting={submitting} />}
          </div>
        </div>

        <FurtherReading topic="triage" className="mt-6" />
      </main>
    </div>
  );
}
